import { TOKEN_DEFINITIONS } from "./registry";
import type { RecognizedToken, TokenDefinition, TokenMatch } from "./types";

/**
 * Load-time token discovery. Rebuilding the editor document depends only on `html`/`message`, so
 * this is where stored bodies are scanned for `{{…}}` tokens, `{url}[text]` link markup, and the
 * legacy `{name}`/`{sender}` tokens. Matching is deliberately generic and **derived from the
 * registry itself** — each definition supplies its own matcher via `build()`, so adding or changing
 * a token needs no edits here. Any well-formed token that matches no definition is preserved as a
 * graceful `unknown` fallback rather than dropped or corrupted. The matched substring (`raw`) is
 * always kept verbatim so colons, `<style>` suffixes, `$LINK_ONLY`, and unknown tokens round-trip.
 */

/** `{url}[text]` link markup (single-brace url, optional whitespace, bracketed display text). */
const LINK_RE = /\{[^{}]+\}\s*\[[^[\]]*\]/g;
/** `{{ … }}` double-curly token. */
const DOUBLE_RE = /\{\{[\s\S]*?\}\}/g;
/** Legacy single-curly name tokens. */
const LEGACY_RE = /\{name\}|\{sender\}/g;

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
/** Wrap a param key in printable delimiters no real token contains, so it survives regex-escaping. */
const sentinel = (key: string): string => `@@${key}@@`;

/** Param-less definitions resolve by exact string equality — `build()` is their canonical form. */
const EXACT_MATCHES = new Map<string, TokenDefinition>(
  TOKEN_DEFINITIONS.flatMap((def): [string, TokenDefinition][] => (def.params ? [] : [[def.build(), def]]))
);

interface ParamMatcher {
  def: TokenDefinition;
  regex: RegExp;
  /** Count of literal (non-captured) characters — used to try the most specific matcher first. */
  specificity: number;
}

/** Turn a parameterized definition into an anchored regex by building it with sentinel arguments. */
const toParamMatcher = (def: TokenDefinition): ParamMatcher => {
  const params = def.params ?? [];
  const sample = def.build(Object.fromEntries(params.map((p) => [p.key, sentinel(p.key)])));
  const pattern = params.reduce((acc, p, index) => {
    // The display text (always last) may contain anything incl. colons; earlier params are lazy.
    const capture = p.kind === "displayText" || index === params.length - 1 ? "[\\s\\S]*" : "[\\s\\S]+?";
    return acc.replace(sentinel(p.key), `(${capture})`);
  }, escapeRegExp(sample));
  const specificity = sample.replace(/@@[^@]*@@/g, "").length;
  return { def, regex: new RegExp(`^${pattern}$`), specificity };
};

const PARAM_MATCHERS: ParamMatcher[] = TOKEN_DEFINITIONS.flatMap((def) =>
  def.params ? [toParamMatcher(def)] : []
).sort((a, b) => b.specificity - a.specificity);

/** Sentinel display text (plain `{url}[…]` link) that means "no display text" — mapped back to "". */
const LINK_ONLY_SENTINEL = "$LINK_ONLY";

/**
 * The reverse of a parameterized definition's `build()`: given an exact token string, recover the
 * definition and the param values it was built from (keyed by param key). Returns null for
 * param-less, unknown, or non-matching tokens. The plain-link `$LINK_ONLY` sentinel maps back to an
 * empty display text so the edit collector shows that field blank. Backs the edit-in-place pre-fill
 * and the hover preview.
 */
export const extractTokenParams = (
  raw: string
): { definition: TokenDefinition; values: Record<string, string> } | null => {
  const matcher = PARAM_MATCHERS.find((m) => m.regex.test(raw));
  if (!matcher) return null;
  const captures = matcher.regex.exec(raw);
  if (!captures) return null;
  const params = matcher.def.params ?? [];
  const values = params.reduce<Record<string, string>>((acc, param, index) => {
    const captured = captures[index + 1] ?? "";
    acc[param.key] = param.kind === "displayText" && captured === LINK_ONLY_SENTINEL ? "" : captured;
    return acc;
  }, {});
  return { definition: matcher.def, values };
};

/** Namespaces that expose dotted value tokens — derived from the registry, not enumerated. */
const VALUE_NAMESPACES = new Set(
  TOKEN_DEFINITIONS.flatMap((def) =>
    def.kind === "value" && def.id.startsWith(`${def.namespace}.`) ? [def.namespace] : []
  )
);

/** Replace the `&amp;` HTML entity with `&` (the HTML editor may escape `&` in field names). */
export const normalizeAmpersandEntities = (value: string): string => value.replace(/&amp;/g, "&");

/** Classify a single exact token string (a `{{…}}`, `{url}[text]`, or legacy `{name}`/`{sender}`). */
export const classifyToken = (raw: string): RecognizedToken => {
  const exact = EXACT_MATCHES.get(raw);
  if (exact) return { raw, kind: exact.kind, definitionId: exact.id };

  const matcher = PARAM_MATCHERS.find((m) => m.regex.test(raw));
  if (matcher) return { raw, kind: matcher.def.kind, definitionId: matcher.def.id };

  // A `{{namespace.field}}` in a known value namespace is a recognized (custom) field with no
  // dedicated definition; anything else is a preserved-but-unknown fallback chip.
  const valueMatch = /^\{\{([a-zA-Z_]+)\.[\s\S]+\}\}$/.exec(raw);
  if (valueMatch && VALUE_NAMESPACES.has(valueMatch[1] ?? "")) {
    return { raw, kind: "value" };
  }
  return { raw, kind: "unknown" };
};

interface RawMatch {
  start: number;
  end: number;
  raw: string;
}

const collect = (text: string, re: RegExp): RawMatch[] =>
  Array.from(text.matchAll(re), (m) => ({ start: m.index ?? 0, end: (m.index ?? 0) + m[0].length, raw: m[0] }));

const overlaps = (candidate: RawMatch, accepted: RawMatch[]): boolean =>
  accepted.some((other) => candidate.start < other.end && other.start < candidate.end);

const acceptNonOverlapping = (accepted: RawMatch[], candidate: RawMatch): RawMatch[] =>
  overlaps(candidate, accepted) ? accepted : [...accepted, candidate];

/**
 * Find every token in `text`, in source order, with its position. Link markup and double-curly
 * tokens take precedence over legacy single-curly tokens where they would overlap.
 */
export const findTokens = (text: string): TokenMatch[] => {
  if (!text) return [];
  const primary = [...collect(text, LINK_RE), ...collect(text, DOUBLE_RE)].reduce<RawMatch[]>(acceptNonOverlapping, []);
  const accepted = collect(text, LEGACY_RE).reduce<RawMatch[]>(acceptNonOverlapping, primary);

  return [...accepted]
    .sort((a, b) => a.start - b.start)
    .map(({ start, end, raw }) => ({ start, end, token: classifyToken(raw) }));
};
